import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, Building2, Globe, ArrowRight, Check, User, Gift } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PhoneInput from "@/components/PhoneInput";
import axios from "axios";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: form, 2: verify
  const [countries, setCountries] = useState({});
  const [phoneValid, setPhoneValid] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    confirm_password: "",
    mobile: "",
    company_name: "",
    country: ""
  });
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    fetchCountries();
    // Check for referral code in URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
    }
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/countries`);
      setCountries(response.data.countries || {});
    } catch (error) {
      console.error("Failed to fetch countries");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (!formData.country) {
      toast.error("Please select your country");
      return;
    }
    
    if (!phoneValid && formData.mobile) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/signup`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Track referral if there's a referral code
        if (referralCode) {
          try {
            await axios.post(`${BACKEND_URL}/api/referrals/track-signup`, null, {
              params: {
                referral_code: referralCode,
                referred_email: formData.email
              }
            });
          } catch (refError) {
            console.log("Referral tracking:", refError.response?.data?.reason || "failed");
          }
        }
        
        setStep(2);
        setResendCooldown(60);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || "Signup failed");
      } else {
        toast.error(detail || "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/verify-email`,
        { email: formData.email, otp },
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success("Email verified! Welcome to SeatPilot.");
        navigate("/dashboard", { state: { user: response.data.user } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-otp`, { email: formData.email });
      toast.success("New verification code sent!");
      setResendCooldown(60);
    } catch (error) {
      toast.error("Failed to resend code");
    }
  };

  const handleGoogleSignup = () => {
    // Redirect to backend Google OAuth endpoint
    const backendUrl = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
    if (!backendUrl) {
      toast.error('Configuration error. Please try again later.');
      return;
    }
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  const passwordRequirements = [
    { label: "At least 8 characters", met: formData.password.length >= 8 },
    { label: "One uppercase letter", met: /[A-Z]/.test(formData.password) },
    { label: "One lowercase letter", met: /[a-z]/.test(formData.password) },
    { label: "One number", met: /\d/.test(formData.password) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-violet-600 to-indigo-700 p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">SP</span>
            </div>
            <span className="text-2xl font-bold text-white">SeatPilot</span>
          </Link>
        </div>
        
        <div className="text-white">
          <h1 className="text-4xl font-bold mb-4">Start your free trial</h1>
          <p className="text-violet-200 text-lg mb-8">
            Join thousands of event organizers who trust SeatPilot for their seating management.
          </p>
          
          <div className="bg-white/10 backdrop-blur rounded-xl p-6">
            <h3 className="font-semibold text-lg mb-4">Free trial includes:</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span>1 month free access</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span>Up to 500 seats</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span>Full API access</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span>No credit card required</span>
              </li>
            </ul>
          </div>
        </div>
        
        <p className="text-violet-300 text-sm">
          Powered by TicketKart &copy; {new Date().getFullYear()}
        </p>
      </div>
      
      {/* Right Side - Signup Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">SP</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">SeatPilot</span>
            </Link>
          </div>
          
          <Card className="p-8 shadow-xl border-0">
            {step === 1 ? (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Create your account</h2>
                  <p className="text-gray-500">Start your 1-month free trial</p>
                </div>
                
                {/* Referral Banner */}
                {referralCode && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg">
                    <div className="flex items-center gap-2 text-violet-700">
                      <Gift className="w-5 h-5" />
                      <span className="font-medium">You've been referred!</span>
                    </div>
                    <p className="text-sm text-violet-600 mt-1">
                      Sign up and subscribe to get <strong>referral credit</strong> for White-Label!
                    </p>
                    <div className="mt-2 text-xs text-violet-500">
                      Referral Code: <code className="bg-violet-100 px-2 py-0.5 rounded">{referralCode}</code>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Smith"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="you@company.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                    <PhoneInput
                      value={formData.mobile}
                      onChange={(phone) => setFormData({...formData, mobile: phone})}
                      onValidation={setPhoneValid}
                      autoDetectCountry={true}
                    />
                    {!phoneValid && formData.mobile && (
                      <p className="text-xs text-red-500 mt-1">Please enter a valid phone number</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name (Optional)</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        placeholder="Your Company Ltd"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                    <Select 
                      value={formData.country} 
                      onValueChange={(value) => setFormData({...formData, country: value})}
                    >
                      <SelectTrigger>
                        <Globe className="w-5 h-5 text-gray-400 mr-2" />
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(countries).map(([code, info]) => (
                          <SelectItem key={code} value={code}>
                            {info.name} ({info.symbol})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Create a strong password"
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {formData.password && (
                      <div className="mt-2 space-y-1">
                        {passwordRequirements.map((req, i) => (
                          <div key={i} className={`flex items-center gap-2 text-xs ${req.met ? 'text-green-600' : 'text-gray-400'}`}>
                            <Check className={`w-3 h-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                            {req.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="password"
                        value={formData.confirm_password}
                        onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                        placeholder="Confirm your password"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Referral Code Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referral Code <span className="text-gray-400">(optional)</span>
                    </label>
                    <div className="relative">
                      <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        placeholder="SP-XXXXXXXX"
                        className="pl-10 uppercase"
                      />
                    </div>
                    {referralCode && (
                      <p className="text-xs text-violet-600 mt-1">
                        Get referral credit when you subscribe!
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <input type="checkbox" required className="mt-1 rounded border-gray-300 text-violet-600" />
                    <span className="text-sm text-gray-600">
                      I agree to the{" "}
                      <Link to="/terms" className="text-violet-600 hover:underline">Terms of Service</Link>
                      {" "}and{" "}
                      <Link to="/privacy" className="text-violet-600 hover:underline">Privacy Policy</Link>
                    </span>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
                    disabled={loading}
                  >
                    {loading ? "Creating account..." : "Create account"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Or sign up with</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignup}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
                
                <p className="text-center text-sm text-gray-600 mt-6">
                  Already have an account?{" "}
                  <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">
                    Sign in
                  </Link>
                </p>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-violet-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify your email</h2>
                  <p className="text-gray-500">
                    We sent a 6-digit code to<br />
                    <span className="font-medium text-gray-900">{formData.email}</span>
                  </p>
                </div>
                
                <form onSubmit={handleVerify} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600"
                    disabled={loading || otp.length !== 6}
                  >
                    {loading ? "Verifying..." : "Verify Email"}
                  </Button>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendCooldown > 0}
                      className={`text-sm font-medium ${resendCooldown > 0 ? 'text-gray-400' : 'text-violet-600 hover:text-violet-700'}`}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full text-sm text-gray-600 hover:text-gray-900"
                  >
                    Back to signup
                  </button>
                </form>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
